import { LinearProgress } from '@material-ui/core';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import { useResponsiveValue } from '@superdispatch/ui';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import {
  Children,
  CSSProperties,
  forwardRef,
  HTMLAttributes,
  MutableRefObject,
  ReactElement,
  Ref,
  useRef,
} from 'react';
import styled from 'styled-components';

const ListBoxProgress = styled(LinearProgress)`
  left: 0;
  right: 0;
  bottom: 8px;
  position: absolute;
`;

interface ListBoxRowProps {
  item: ReactElement;
  style: CSSProperties;
}

function ListBoxRow({ item, style }: ListBoxRowProps) {
  // Autocomplete passed `li` elements, but we want to use `div` elements and
  // just pass `li` props.
  return <div {...item.props} style={style} />;
}

interface ListBoxProps extends HTMLAttributes<HTMLElement> {
  loading?: boolean;
  onScrollEnd?: () => void;
  listRef?: Ref<Virtualizer<HTMLDivElement, Element>>;
  calculateItemSize?: (index: number, responsiveSize: number) => number;
}

const ListBox = forwardRef<HTMLDivElement, ListBoxProps>(
  (
    { children, loading, onScrollEnd, calculateItemSize, listRef, ...props },
    ref,
  ) => {
    const items = Children.toArray(children) as ReactElement[];
    const responsiveItemSize = useResponsiveValue(48, 36);
    const height = Math.min(items.length, 8) * responsiveItemSize;
    const scrollRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
      count: items.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: (index) =>
        calculateItemSize?.(index, responsiveItemSize) ?? responsiveItemSize,
      overscan: 2,
      onChange: (instance) => {
        if (listRef) {
          if (typeof listRef === 'function') {
            listRef(instance);
          } else {
            (
              listRef as MutableRefObject<Virtualizer<HTMLDivElement, Element>>
            ).current = instance;
          }
        }

        const virtualItems = instance.getVirtualItems();
        const lastItem = virtualItems[virtualItems.length - 1];
        if (lastItem && lastItem.index === items.length - 1) {
          onScrollEnd?.();
        }
      },
    });

    return (
      <div ref={ref} {...props}>
        {loading && <ListBoxProgress />}
        {/* eslint-disable-next-line react/forbid-dom-props */}
        <div
          ref={scrollRef}
          style={{ width: '100%', height, overflowY: 'auto' }}
        >
          {/* eslint-disable-next-line react/forbid-dom-props */}
          <div
            style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = items[virtualRow.index];
              if (!item) return null;
              return (
                <ListBoxRow
                  key={virtualRow.key}
                  item={item}
                  // eslint-disable-next-line react/forbid-component-props
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    height: virtualRow.size,
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  },
);

ListBox.displayName = 'ListBox';

export type VirtualizedAutocompleteProps<
  T,
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
> = Omit<
  AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>,
  'ListboxComponent' | 'ListboxProps'
> &
  Pick<ListBoxProps, 'onScrollEnd' | 'listRef' | 'calculateItemSize'>;

export function VirtualizedAutocomplete<
  T,
  TMultiple extends boolean | undefined,
  TDisableClearable extends boolean | undefined,
  TFreeSolo extends boolean | undefined,
>({
  listRef,
  loading,
  onScrollEnd,
  calculateItemSize,
  ...props
}: VirtualizedAutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>) {
  return (
    <Autocomplete
      {...props}
      loading={loading}
      ListboxComponent={ListBox}
      ListboxProps={{ listRef, loading, onScrollEnd, calculateItemSize }}
    />
  );
}
